import store from '../store'
import axios from 'axios'

// Status codes que indican sesion invalida (token vencido, basura o ausente).
// El backend de CAJAPSIPBA devuelve 403 para token invalido; 401 se incluye
// por seguridad ante cambios futuros o endpoints que usen el estandar REST.
const STATUS_SESION_INVALIDA = [401, 403]

// URLs que NO deben disparar cierre de sesion automatico.
// - login: el error de credenciales invalidas lo maneja el action de login.
// - logout: evita loops si el propio /logout devuelve 401/403.
// - loginAnillo: el 401 (TTL de operador vencido) lo maneja especificamente
//   el catch de entrarComoAfiliado (gestion.js); si el interceptor generico
//   tambien reaccionara, habria doble manejo/doble snack.
const URLS_EXCLUIDAS_SESION = ['login', 'logout', 'loginAnillo']

// Guarda idempotente: evita que multiples 401 simulataneos (ej. requests
// en vuelo cuando la sesion ya fue pisada) disparen cerrarSesion varias veces.
let isLoggingOut = false

axios.defaults.headers['Content-Type'] = 'application/json';

axios.interceptors.request.use(function (config) {
    let token = sessionStorage.getItem('token')
    if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
    }
    return config;
})

axios.interceptors.response.use((response) => {
    return response;
}, (error) => {
    // --- 503 SESSION_STORE_UNAVAILABLE: retry con backoff exponencial ---
    if (error && error.response && error.response.status === 503) {
        const errorCode = error.response.data && error.response.data.errorCode
        if (errorCode === 'SESSION_STORE_UNAVAILABLE') {
            error.config.__retryCount = error.config.__retryCount || 0
            if (error.config.__retryCount < 3) {
                error.config.__retryCount++
                const delays = [2000, 5000, 10000]
                const delay = delays[error.config.__retryCount - 1]
                // Opcional: mostrar notificacion al usuario en el primer retry. comentado momentaneamente para evitar confusión, pero puede ser útil si el backend tarda mucho en recuperarse.
                /*                 if (error.config.__retryCount === 1) {
                                    store.dispatch('notificaciones/showSnack', {
                                        message: 'Problema temporal con el servicio, reintentando...',
                                        type: '500',
                                    }, { root: true })
                                } */
                return new Promise(resolve => setTimeout(resolve, delay))
                    .then(() => axios(error.config))
            }
            store.dispatch('notificaciones/showSnack', {
                message: 'No podemos conectarnos al servidor. Si el problema persiste, contactá a soporte.',
                type: '400',
            }, { root: true })
            return Promise.reject(error)
        }
    }

    // --- 401/403: sesion invalida ---
    if (error && error.response && STATUS_SESION_INVALIDA.includes(error.response.status)) {
        const url = (error.config && error.config.url) || ''
        const esExcluida = URLS_EXCLUIDAS_SESION.some(ruta => url.endsWith('/' + ruta))

        // Caso especial: rechazo del middleware Auth en modo gestion. El
        // back devuelve 403 pero la sesion sigue siendo valida — solo es
        // una operacion no autorizada para el operador. No cerramos sesion;
        // mostramos snack y dejamos que el caller maneje el error.
        const mensaje = (error.response.data && error.response.data.message) || ''
        const esRechazoModoGestion = mensaje.toLowerCase().includes('modo gestión')
            || mensaje.toLowerCase().includes('modo gestion')
        if (esRechazoModoGestion) {
            store.dispatch('notificaciones/showSnack', {
                message: 'No tenés permiso para realizar esta acción en modo lectura.',
                type: '400',
            }, { root: true })
            error.handledByInterceptor = true
            return Promise.reject(error)
        }

        if (!esExcluida && sessionStorage.getItem('token') && !isLoggingOut) {
            const errorCode = error.response.data && error.response.data.errorCode

            if (errorCode === 'SESSION_SUPERSEDED') {
                isLoggingOut = true
                store.dispatch('notificaciones/showSnack', {
                    message: 'Tu sesión se cerró. Iniciaste sesión en otro dispositivo.',
                    type: '400',
                }, { root: true })
                store.dispatch('auth/cerrarSesion').finally(() => { isLoggingOut = false })
            } else if (errorCode === 'SESSION_MISSING_SID') {
                isLoggingOut = true
                store.dispatch('notificaciones/showSnack', {
                    message: 'Por una actualización del sistema, necesitamos que vuelvas a iniciar sesión.',
                    type: '400',
                }, { root: true })
                store.dispatch('auth/cerrarSesion').finally(() => { isLoggingOut = false })
            } else {
                // 401/403 sin errorCode conocido: comportamiento legacy
                isLoggingOut = true
                store.dispatch('auth/cerrarSesion').finally(() => { isLoggingOut = false })
            }
        }
    }
    return Promise.reject(error);
})

export default axios;
